/**
 * Cartão de crédito — máscara, detecção de bandeira e validação local.
 *
 * O legado não validava nada no cliente: o `cartao_credito.blade.php` só
 * aplicava máscaras com jQuery Mask (`0000 0000 0000 0000`, `00/0000`, `0000`)
 * e mandava o formulário direto para o servidor, que repassava à API. Cada
 * cartão digitado errado virava uma ida e volta na API de tokenização.
 *
 * Aqui a validação acontece antes da chamada — Luhn, comprimento por bandeira,
 * validade e CVV — porque:
 *
 * 1. reduz chamadas com dado sabidamente inválido à API PCI;
 * 2. dá erro no campo certo, em vez de um alerta genérico.
 *
 * O que NÃO muda: a máscara de validade continua `MM/AAAA` (quatro dígitos de
 * ano), que é o formato esperado no campo `mes_ano_vencimento` da API Contel.
 *
 * Nada deste módulo guarda, loga ou serializa o PAN — ele só transforma
 * strings em memória.
 */

import { onlyDigits } from './phone';

export type CardBrand =
  | 'visa'
  | 'mastercard'
  | 'amex'
  | 'elo'
  | 'hipercard'
  | 'diners'
  | 'discover'
  | 'unknown';

export interface CardBrandSpec {
  id: CardBrand;
  /**
   * Nome da bandeira. É marca registrada (nome próprio), não texto de
   * interface: não passa pelo i18n e é igual em qualquer idioma.
   */
  label: string;
  /** Comprimentos de PAN aceitos pela bandeira. */
  lengths: number[];
  /** Amex usa CVV de 4 dígitos; as demais, 3. */
  cvvLength: number;
  /** Posições (em dígitos) onde a máscara insere espaço. */
  gaps: number[];
}

/** Faixa numérica de prefixo, comparada com os N primeiros dígitos. */
type PrefixRange = [number, number];

interface BrandRule extends CardBrandSpec {
  /** Quantidade de dígitos do prefixo avaliado por esta regra. */
  prefixSize: number;
  ranges: PrefixRange[];
}

const DEFAULT_GAPS = [4, 8, 12, 16];
/** Amex (4-6-5) e Diners de 14 dígitos (4-6-4) usam o mesmo agrupamento. */
const AMEX_GAPS = [4, 10];

/**
 * Regras em ordem de especificidade: a primeira que casa vence.
 *
 * Elo e Hipercard vêm antes de Visa/Mastercard/Diners/Discover porque emitem
 * BINs dentro das faixas delas (ex.: `401178` é Elo, não Visa; `3841` é
 * Hipercard, não Diners).
 */
const BRAND_RULES: BrandRule[] = [
  {
    id: 'elo',
    label: 'Elo',
    lengths: [16],
    cvvLength: 3,
    gaps: DEFAULT_GAPS,
    prefixSize: 6,
    ranges: [
      [401178, 401179],
      [431274, 431274],
      [438935, 438935],
      [451416, 451416],
      [457393, 457393],
      [457631, 457632],
      [504175, 504175],
      [506699, 506778],
      [509000, 509999],
      [627780, 627780],
      [636297, 636297],
      [636368, 636368],
      [650031, 650033],
      [650035, 650051],
      [650405, 650439],
      [650485, 650538],
      [650541, 650598],
      [650700, 650718],
      [650720, 650727],
      [650901, 650978],
      [651652, 651679],
      [655000, 655019],
      [655021, 655058],
    ],
  },
  {
    id: 'hipercard',
    label: 'Hipercard',
    lengths: [16, 19],
    cvvLength: 3,
    gaps: DEFAULT_GAPS,
    prefixSize: 6,
    ranges: [[606282, 606282]],
  },
  {
    id: 'hipercard',
    label: 'Hipercard',
    lengths: [16, 19],
    cvvLength: 3,
    gaps: DEFAULT_GAPS,
    prefixSize: 4,
    ranges: [[3841, 3841]],
  },
  {
    id: 'amex',
    label: 'American Express',
    lengths: [15],
    cvvLength: 4,
    gaps: AMEX_GAPS,
    prefixSize: 2,
    ranges: [
      [34, 34],
      [37, 37],
    ],
  },
  {
    id: 'diners',
    label: 'Diners Club',
    lengths: [14, 16, 19],
    cvvLength: 3,
    gaps: AMEX_GAPS,
    prefixSize: 3,
    ranges: [[300, 305]],
  },
  {
    id: 'diners',
    label: 'Diners Club',
    lengths: [14, 16, 19],
    cvvLength: 3,
    gaps: AMEX_GAPS,
    prefixSize: 2,
    ranges: [
      [36, 36],
      [38, 39],
    ],
  },
  {
    id: 'mastercard',
    label: 'Mastercard',
    lengths: [16],
    cvvLength: 3,
    gaps: DEFAULT_GAPS,
    prefixSize: 4,
    ranges: [[2221, 2720]],
  },
  {
    id: 'mastercard',
    label: 'Mastercard',
    lengths: [16],
    cvvLength: 3,
    gaps: DEFAULT_GAPS,
    prefixSize: 2,
    ranges: [[51, 55]],
  },
  {
    id: 'discover',
    label: 'Discover',
    lengths: [16, 19],
    cvvLength: 3,
    gaps: DEFAULT_GAPS,
    prefixSize: 6,
    ranges: [[622126, 622925]],
  },
  {
    id: 'discover',
    label: 'Discover',
    lengths: [16, 19],
    cvvLength: 3,
    gaps: DEFAULT_GAPS,
    prefixSize: 4,
    ranges: [[6011, 6011]],
  },
  {
    id: 'discover',
    label: 'Discover',
    lengths: [16, 19],
    cvvLength: 3,
    gaps: DEFAULT_GAPS,
    prefixSize: 3,
    ranges: [[644, 649]],
  },
  {
    id: 'discover',
    label: 'Discover',
    lengths: [16, 19],
    cvvLength: 3,
    gaps: DEFAULT_GAPS,
    prefixSize: 2,
    ranges: [[65, 65]],
  },
  {
    id: 'visa',
    label: 'Visa',
    lengths: [13, 16, 19],
    cvvLength: 3,
    gaps: DEFAULT_GAPS,
    prefixSize: 1,
    ranges: [[4, 4]],
  },
];

/** Bandeira ainda não identificada: aceita os comprimentos usuais de PAN. */
const UNKNOWN_BRAND: CardBrandSpec = {
  id: 'unknown',
  label: '',
  lengths: [13, 14, 15, 16, 17, 18, 19],
  cvvLength: 3,
  gaps: DEFAULT_GAPS,
};

/** Maior PAN aceito — usado como limite do campo enquanto a bandeira é desconhecida. */
export const MAX_CARD_DIGITS = 19;

function matchesRule(digits: string, rule: BrandRule): boolean {
  // Só avalia quando já há dígitos suficientes para o prefixo da regra; assim
  // um "4" isolado é Visa e só vira Elo quando o sexto dígito confirmar.
  if (digits.length < rule.prefixSize) return false;
  const prefix = Number(digits.slice(0, rule.prefixSize));
  if (!Number.isFinite(prefix)) return false;
  return rule.ranges.some(([start, end]) => prefix >= start && prefix <= end);
}

/** Identifica a bandeira pelo prefixo; devolve a spec "unknown" quando não reconhece. */
export function detectCardBrand(value: string): CardBrandSpec {
  const digits = onlyDigits(value);
  if (!digits) return UNKNOWN_BRAND;

  for (const rule of BRAND_RULES) {
    if (matchesRule(digits, rule)) {
      const { id, label, lengths, cvvLength, gaps } = rule;
      return { id, label, lengths, cvvLength, gaps };
    }
  }

  return UNKNOWN_BRAND;
}

/** Maior comprimento de PAN aceito pela bandeira detectada. */
export function maxCardDigits(brand: CardBrandSpec): number {
  return brand.lengths.reduce((max, length) => Math.max(max, length), 0) || MAX_CARD_DIGITS;
}

/**
 * Aplica a máscara do cartão respeitando o agrupamento da bandeira
 * (`0000 0000 0000 0000` no caso geral, `0000 000000 00000` na Amex).
 */
export function formatCardNumber(value: string): string {
  const brand = detectCardBrand(value);
  const digits = onlyDigits(value).slice(0, maxCardDigits(brand));

  let formatted = '';
  let cursor = 0;
  for (const gap of brand.gaps) {
    // O espaço só entra quando existe dígito depois dele: sem isso o usuário
    // não conseguiria apagar o último dígito de um grupo.
    if (digits.length > gap) {
      formatted += `${digits.slice(cursor, gap)} `;
      cursor = gap;
    }
  }
  return formatted + digits.slice(cursor);
}

/** Algoritmo de Luhn (dígito verificador do PAN). */
export function passesLuhn(value: string): boolean {
  const digits = onlyDigits(value);
  if (digits.length < 2) return false;

  let sum = 0;
  let double = false;
  for (let index = digits.length - 1; index >= 0; index -= 1) {
    let digit = Number(digits.charAt(index));
    if (double) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
    double = !double;
  }
  return sum % 10 === 0;
}

/** Comprimento compatível com a bandeira + Luhn. */
export function isValidCardNumber(value: string): boolean {
  const digits = onlyDigits(value);
  const brand = detectCardBrand(digits);
  if (!brand.lengths.includes(digits.length)) return false;
  return passesLuhn(digits);
}

/** Aplica a máscara `MM/AAAA` progressivamente. */
export function formatExpiry(value: string): string {
  const digits = onlyDigits(value).slice(0, 6);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}/${digits.slice(2)}`;
}

export type ExpiryStatus = 'valid' | 'invalid' | 'expired';

/**
 * Valida `MM/AAAA`: mês entre 1 e 12 e vencimento a partir do mês corrente
 * (um cartão vence no ÚLTIMO dia do mês impresso, então o próprio mês ainda vale).
 */
export function checkExpiry(value: string, reference: Date = new Date()): ExpiryStatus {
  const digits = onlyDigits(value);
  if (digits.length !== 6) return 'invalid';

  const month = Number(digits.slice(0, 2));
  const year = Number(digits.slice(2));
  if (!Number.isFinite(month) || !Number.isFinite(year)) return 'invalid';
  if (month < 1 || month > 12) return 'invalid';

  const currentYear = reference.getFullYear();
  const currentMonth = reference.getMonth() + 1;
  if (year < currentYear) return 'expired';
  if (year === currentYear && month < currentMonth) return 'expired';

  return 'valid';
}

/** Mantém só dígitos no CVV, limitado ao tamanho da bandeira. */
export function formatCvv(value: string, brand: CardBrandSpec): string {
  return onlyDigits(value).slice(0, brand.cvvLength);
}

/** 3 dígitos, ou 4 na Amex. */
export function isValidCvv(value: string, brand: CardBrandSpec): boolean {
  return onlyDigits(value).length === brand.cvvLength;
}

/** Nome impresso no cartão: basta não ser vazio, como no `required` do legado. */
export function isValidCardHolder(value: string): boolean {
  return value.trim().length > 0;
}

export type CardField = 'name' | 'number' | 'expiry' | 'cvv';

/** Chaves de `t.creditCard.errors` — o componente traduz, este módulo não. */
export type CardErrorCode =
  | 'invalidName'
  | 'invalidNumber'
  | 'invalidExpiry'
  | 'expiredCard'
  | 'invalidCvv';

export interface CardValidationIssue {
  field: CardField;
  code: CardErrorCode;
}

export interface CardFormInput {
  holder: string;
  number: string;
  expiry: string;
  cvv: string;
}

/**
 * Valida o formulário inteiro e devolve o PRIMEIRO problema encontrado, na
 * ordem em que os campos aparecem na tela, ou `null` quando está tudo certo.
 */
export function validateCardForm(input: CardFormInput): CardValidationIssue | null {
  if (!isValidCardHolder(input.holder)) {
    return { field: 'name', code: 'invalidName' };
  }
  if (!isValidCardNumber(input.number)) {
    return { field: 'number', code: 'invalidNumber' };
  }

  const expiry = checkExpiry(input.expiry);
  if (expiry === 'invalid') return { field: 'expiry', code: 'invalidExpiry' };
  if (expiry === 'expired') return { field: 'expiry', code: 'expiredCard' };

  if (!isValidCvv(input.cvv, detectCardBrand(input.number))) {
    return { field: 'cvv', code: 'invalidCvv' };
  }

  return null;
}
